import React, { useCallback } from 'react';

import { HeadingItem, Section } from '@atlaskit/menu';

import { useTopMenus } from '@atlassian/jira-navigation-apps-common/src/controllers/top-menus/index.tsx';
import { MENU_ID } from '@atlassian/jira-navigation-apps-common/src/constants.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { EditionAwarenessStandardPubTouchpoint } from '@atlassian/edition-awareness-standard-pub-touchpoint';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	useAnalyticsEvents,
	ContextualAnalyticsData,
	FireScreenAnalytics,
	INLINE_DIALOG,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { useIsEditionAwarenessPillDismissed } from '../../services/is-ea-pill-dismissed/index.tsx';
import { useIsEligibleForUpsellEa } from '../../services/is-eligible-for-upsell-ea/index.tsx';
import { StandardToPremiumModalEntrypointTrigger } from '../modal/trigger.tsx';
import { useProductAndEdition } from '../../services/use-product-and-edition/index.tsx';
import messages from './messages.tsx';

function StandardToPremiumUpsellPubTouchpointImpl() {
	const [, { off: closeMenu }] = useTopMenus(MENU_ID.PROFILE);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const isSiteAdmin = useIsSiteAdmin();
	const cloudId = useCloudId();
	const { product, edition } = useProductAndEdition();
	const { isEligibleForUpsellEa, cohort } = useIsEligibleForUpsellEa({
		product,
		edition,
		isSiteAdmin,
	});

	const {
		hasFetched: hasFetchedDismissedEditionAwarenessPillState,
		isDismissed: hasDismissedEditionAwarenessPill,
	} = useIsEditionAwarenessPillDismissed();

	const handleOnClick = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			}),
			'editionAwarenessStandardToPremiumPUB',
		);

		closeMenu();
	}, [closeMenu, createAnalyticsEvent]);

	let isPaidStandard = false;
	if (edition === 'standard') {
		if (product === 'jira') {
			isPaidStandard = fg('edition_awareness_upsell_jira_fg');
		}
		if (product === 'jira-service-management') {
			isPaidStandard = fg('edition_awareness_upsell_jsm_fg');
		}
	}

	const shouldShowTouchpoint =
		isEligibleForUpsellEa &&
		cohort === 'treatment' &&
		isPaidStandard &&
		hasFetchedDismissedEditionAwarenessPillState &&
		hasDismissedEditionAwarenessPill &&
		product;

	if (shouldShowTouchpoint) {
		return (
			<ContextualAnalyticsData
				sourceName="editionAwarenessStandardToPremiumPUB"
				sourceType={INLINE_DIALOG}
			>
				<Section hasSeparator>
					<HeadingItem>{formatMessage(messages.upgrade)}</HeadingItem>
					<StandardToPremiumModalEntrypointTrigger
						cloudId={cloudId}
						product={product}
						onClick={handleOnClick}
					>
						<EditionAwarenessStandardPubTouchpoint product={product} />
					</StandardToPremiumModalEntrypointTrigger>
				</Section>
				<FireScreenAnalytics
					attributes={{
						cohort,
						flagKey: 'edition_awareness_upsell_jira_ab',
					}}
				/>
			</ContextualAnalyticsData>
		);
	}

	return null;
}

function StandardToPremiumPubTouchpointWithErrorBoundary() {
	return (
		<JSErrorBoundary
			id="editionAwarenessStandardToPremiumPubTouchpoint"
			packageName="jiraEditionAwarenessForStandardToPremium"
			fallback="unmount"
			teamName="luna-growth"
		>
			<StandardToPremiumUpsellPubTouchpointImpl />
		</JSErrorBoundary>
	);
}

export const StandardToPremiumPubTouchpoint = componentWithFG(
	'edition_awareness_upsell_jira_ab_fg',
	StandardToPremiumPubTouchpointWithErrorBoundary,
	() => <></>,
);
